import { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Plus, Building2, Home, XCircle, RefreshCw } from 'lucide-react';
import { format, addDays, subDays, differenceInDays, isToday, isWeekend, startOfDay } from 'date-fns';

const DAY_W = 46;
const UNIT_COL_W = 190;

const SOURCE_COLORS = {
  manual: { bg: '#2563EB', text: '#fff', label: 'Manual' },
  ical_airbnb: { bg: '#F472B6', text: '#fff', label: 'Airbnb' },
  ical_booking: { bg: '#1E3A5F', text: '#fff', label: 'Booking' },
  ical_vrbo: { bg: '#8B5CF6', text: '#fff', label: 'iCal' },
  ical_google: { bg: '#8B5CF6', text: '#fff', label: 'iCal' },
  ical_other: { bg: '#8B5CF6', text: '#fff', label: 'iCal' },
  block: { bg: '#FB7185', text: '#fff', label: 'Block' },
};

function getSourceStyle(source) {
  if (source?.startsWith('ical_airbnb')) return SOURCE_COLORS.ical_airbnb;
  if (source?.startsWith('ical_booking')) return SOURCE_COLORS.ical_booking;
  if (source?.startsWith('ical_vrbo')) return SOURCE_COLORS.ical_vrbo;
  if (source?.startsWith('ical_')) return SOURCE_COLORS.ical_other;
  if (source === 'block') return SOURCE_COLORS.block;
  return SOURCE_COLORS.manual;
}

function getBarLabel(item) {
  if (item._type === 'block') return item.notes || '(not Available)';
  if (item.guest_name) return item.guest_name;
  const src = item.source || '';
  if (src.includes('airbnb')) return `Closed (airbnb)`;
  if (src.includes('booking')) return `Closed (booking)`;
  if (src.includes('vrbo')) return `Closed (vrbo)`;
  if (src.includes('ical')) return `Closed (iCal)`;
  return item.notes || 'Guest';
}

export default function Reservations() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const scrollRef = useRef(null);
  const canCancel = user?.role === 'admin' || user?.role === 'manager';
  const [calendarData, setCalendarData] = useState([]);
  const [allUnits, setAllUnits] = useState([]);
  const [allGuests, setAllGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [form, setForm] = useState({ unit_id: '', guest_id: '', check_in: '', check_out: '', status: 'confirmed', source: 'manual', notes: '' });

  // Date range: 5 days before today to 30 days after
  const today = useMemo(() => startOfDay(new Date()), []);
  const rangeStart = useMemo(() => subDays(today, 5), [today]);
  const rangeEnd = useMemo(() => addDays(today, 35), [today]);
  const days = useMemo(() => {
    const d = [];
    let current = rangeStart;
    while (current <= rangeEnd) {
      d.push(current);
      current = addDays(current, 1);
    }
    return d;
  }, [rangeStart, rangeEnd]);

  const fetchData = async () => {
    try {
      const startStr = format(rangeStart, 'yyyy-MM-dd');
      const endStr = format(rangeEnd, 'yyyy-MM-dd');
      const [cal, units, guests] = await Promise.all([
        api.get('/calendar-data', { params: { start: startStr, end: endStr } }),
        api.get('/units'),
        api.get('/guests'),
      ]);
      setCalendarData(cal.data);
      setAllUnits(units.data);
      setAllGuests(guests.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, []);

  // Scroll to today on load
  useEffect(() => {
    if (!loading && scrollRef.current) {
      const todayIdx = differenceInDays(today, rangeStart);
      scrollRef.current.scrollLeft = Math.max(0, (todayIdx - 2) * DAY_W);
    }
  }, [loading, today, rangeStart]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/reservations', form);
      setDialogOpen(false);
      setForm({ unit_id: '', guest_id: '', check_in: '', check_out: '', status: 'confirmed', source: 'manual', notes: '' });
      fetchData();
    } catch (err) { console.error(err); }
  };

  const handleBarClick = (bar) => {
    if (bar.id.startsWith('block-')) return;
    setSelectedBooking(bar);
  };

  const handleCancelBooking = async () => {
    if (!selectedBooking) return;
    setCancelLoading(true);
    try {
      await api.post(`/reservations/${selectedBooking.id}/cancel`);
      setSelectedBooking(null);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to cancel booking');
    } finally { setCancelLoading(false); }
  };

  const handleDeleteAndHide = async () => {
    if (!selectedBooking) return;
    const platform = (selectedBooking.source || '').replace('ical_', '');
    if (!window.confirm(`Permanently delete this ${platform} block and hide it from future syncs?\n\nThis only removes it from RentalHub. The original ${platform} platform is unaffected.`)) return;
    setDeleteLoading(true);
    try {
      await api.delete(`/reservations/${selectedBooking.id}`);
      setSelectedBooking(null);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete booking');
    } finally { setDeleteLoading(false); }
  };

  const handleSyncAll = async () => {
    setSyncing(true);
    try {
      await api.post('/ical-sources/sync-all');
      fetchData();
    } catch (err) {
      console.error(err);
    } finally { setSyncing(false); }
  };

  const scrollToToday = () => {
    if (scrollRef.current) {
      const todayIdx = differenceInDays(today, rangeStart);
      scrollRef.current.scrollLeft = Math.max(0, (todayIdx - 2) * DAY_W);
    }
  };

  // Build bar items per unit with lane assignment for overlaps
  const getUnitBars = (unit) => {
    const bars = [];
    (unit.reservations || []).forEach(r => {
      const checkIn = startOfDay(new Date(r.check_in));
      const checkOut = startOfDay(new Date(r.check_out));
      const startOffset = differenceInDays(checkIn, rangeStart);
      const duration = differenceInDays(checkOut, checkIn);
      if (startOffset + duration < 0 || startOffset > days.length) return;
      const clampedStart = Math.max(0, startOffset);
      const clampedEnd = Math.min(days.length, startOffset + duration);
      const style = getSourceStyle(r.source);
      bars.push({
        id: r.id,
        left: clampedStart * DAY_W,
        width: (clampedEnd - clampedStart) * DAY_W,
        ...style,
        label: getBarLabel(r),
        source: r.source,
        status: r.status,
        guest_name: r.guest_name,
        check_in: r.check_in,
        check_out: r.check_out,
        nights: duration,
        unit_name: unit.name,
        notes: r.notes,
        clipLeft: startOffset < 0,
        clipRight: startOffset + duration > days.length,
        _start: clampedStart,
        _end: clampedEnd,
      });
    });
    (unit.blocks || []).forEach(b => {
      const start = startOfDay(new Date(b.start_date));
      const end = startOfDay(new Date(b.end_date));
      const startOffset = differenceInDays(start, rangeStart);
      const duration = differenceInDays(end, start);
      if (startOffset + duration < 0 || startOffset > days.length) return;
      const clampedStart = Math.max(0, startOffset);
      const clampedEnd = Math.min(days.length, startOffset + duration);
      const style = getSourceStyle('block');
      bars.push({
        id: `block-${b.id}`,
        left: clampedStart * DAY_W,
        width: (clampedEnd - clampedStart) * DAY_W,
        ...style,
        label: getBarLabel({ ...b, _type: 'block' }),
        clipLeft: startOffset < 0,
        clipRight: startOffset + duration > days.length,
        _start: clampedStart,
        _end: clampedEnd,
      });
    });

    // Assign vertical lanes to overlapping bars
    bars.sort((a, b) => (a._start || 0) - (b._start || 0));
    const lanes = [];
    bars.forEach(bar => {
      let placed = false;
      for (let i = 0; i < lanes.length; i++) {
        if ((bar._start || 0) >= (lanes[i]._end || 0)) {
          bar._lane = i;
          lanes[i] = bar;
          placed = true;
          break;
        }
      }
      if (!placed) {
        bar._lane = lanes.length;
        lanes.push(bar);
      }
    });

    return { bars, laneCount: Math.max(lanes.length, 1) };
  };

  const totalWidth = UNIT_COL_W + days.length * DAY_W;

  if (loading) return <div className="flex items-center justify-center h-64" data-testid="calendar-loading"><div className="animate-spin w-8 h-8 border-2 border-slate-300 border-t-slate-900 rounded-full" /></div>;

  return (
    <div data-testid="reservations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <h1 className="text-xl sm:text-2xl font-bold font-heading text-slate-900 tracking-tight">Calendar</h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleSyncAll} disabled={syncing} className="text-slate-600 border-slate-200 hover:bg-slate-50 font-medium" data-testid="sync-ical-btn">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${syncing ? 'animate-spin' : ''}`} />{syncing ? 'Syncing...' : 'Sync iCal'}
          </Button>
          <Button variant="outline" size="sm" onClick={scrollToToday} className="text-blue-600 border-blue-200 hover:bg-blue-50 font-medium" data-testid="today-btn">Today</Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-slate-900 hover:bg-slate-800" data-testid="add-reservation-btn"><Plus className="w-4 h-4" /></Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle className="font-heading">New Reservation</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Unit</Label>
                  <Select value={form.unit_id} onValueChange={v => setForm({ ...form, unit_id: v })}>
                    <SelectTrigger data-testid="res-unit-select"><SelectValue placeholder="Select unit" /></SelectTrigger>
                    <SelectContent>{allUnits.map(u => <SelectItem key={u.id} value={u.id}>{u.name} ({u.property_name})</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Guest</Label>
                  <Select value={form.guest_id} onValueChange={v => setForm({ ...form, guest_id: v })}>
                    <SelectTrigger data-testid="res-guest-select"><SelectValue placeholder="Select guest" /></SelectTrigger>
                    <SelectContent>{allGuests.map(g => <SelectItem key={g.id} value={g.id}>{g.first_name} {g.last_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Check-in</Label><Input type="date" value={form.check_in} onChange={e => setForm({ ...form, check_in: e.target.value })} required data-testid="res-checkin-input" /></div>
                  <div><Label>Check-out</Label><Input type="date" value={form.check_out} onChange={e => setForm({ ...form, check_out: e.target.value })} required data-testid="res-checkout-input" /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Status</Label>
                    <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="confirmed">Confirmed</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Source</Label>
                    <Select value={form.source} onValueChange={v => setForm({ ...form, source: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="manual">Manual</SelectItem>
                        <SelectItem value="ical_airbnb">Airbnb</SelectItem>
                        <SelectItem value="ical_booking">Booking.com</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Notes</Label><Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} data-testid="res-notes-input" /></div>
                <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" data-testid="res-submit-btn">Create Reservation</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 mb-4 text-xs" data-testid="calendar-legend">
        {[
          { color: '#2563EB', label: 'Manual' },
          { color: '#F472B6', label: 'Airbnb' },
          { color: '#1E3A5F', label: 'Booking' },
          { color: '#8B5CF6', label: 'iCal' },
        ].map(l => (
          <div key={l.label} className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: l.color }} />
            <span className="text-slate-600 font-medium">{l.label}</span>
          </div>
        ))}
      </div>

      {/* Calendar Timeline */}
      <div
        ref={scrollRef}
        className="bg-white border border-slate-200 rounded-lg overflow-auto"
        style={{ maxHeight: 'calc(100vh - 240px)' }}
        data-testid="calendar-timeline"
      >
        <div style={{ minWidth: totalWidth }}>
          {/* Date header row - sticky top */}
          <div className="flex" style={{ position: 'sticky', top: 0, zIndex: 20 }}>
            {/* Corner cell - sticky both */}
            <div
              className="bg-white border-b border-r border-slate-200 flex items-center justify-center text-xs font-semibold text-slate-500 uppercase tracking-wider"
              style={{ width: UNIT_COL_W, minWidth: UNIT_COL_W, position: 'sticky', left: 0, zIndex: 30 }}
              data-testid="calendar-corner"
            >
              Properties
            </div>
            {/* Day columns */}
            <div className="flex">
              {days.map((day, i) => {
                const isT = isToday(day);
                const isWe = isWeekend(day);
                const isFirstOfMonth = day.getDate() === 1;
                return (
                  <div
                    key={i}
                    className={`border-b border-r border-slate-100 flex flex-col items-center justify-center py-2 ${isWe ? 'bg-slate-50' : 'bg-white'}`}
                    style={{ width: DAY_W, minWidth: DAY_W }}
                    data-testid={`cal-day-${format(day, 'yyyy-MM-dd')}`}
                  >
                    {isFirstOfMonth && <span className="text-[9px] text-blue-600 font-bold leading-none">{format(day, 'MMM')}</span>}
                    <span className={`text-sm font-semibold leading-tight ${isT ? 'bg-blue-600 text-white w-7 h-7 rounded-full flex items-center justify-center' : 'text-slate-800'}`}>
                      {format(day, 'd')}
                    </span>
                    <span className={`text-[10px] leading-none mt-0.5 ${isT ? 'text-blue-600 font-bold' : 'text-slate-400'}`}>{format(day, 'EEEEE')}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Property groups */}
          {calendarData.map(prop => (
            <div key={prop.id}>
              {/* Property header */}
              <div
                className="border-b border-slate-200"
                style={{ minWidth: totalWidth, width: totalWidth }}
              >
                <div
                  className="flex items-center gap-2 px-3 py-2 bg-blue-50"
                  style={{ position: 'sticky', left: 0, zIndex: 20, width: UNIT_COL_W }}
                >
                  <Building2 className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                  <span className="text-sm font-bold text-blue-800 truncate">{prop.name}</span>
                </div>
              </div>

              {/* Unit rows */}
              {prop.units.map(unit => {
                const { bars, laneCount } = getUnitBars(unit);
                const rowH = Math.max(56, laneCount * 28 + 8);
                return (
                  <div key={unit.id} className="flex border-b border-slate-100 hover:bg-slate-50/50 transition-colors" data-testid={`cal-unit-row-${unit.id}`}>
                    {/* Unit info - sticky left */}
                    <div
                      className="bg-white border-r border-slate-200 px-3 py-2 flex items-start gap-2"
                      style={{ width: UNIT_COL_W, minWidth: UNIT_COL_W, position: 'sticky', left: 0, zIndex: 10, minHeight: rowH }}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-slate-900 truncate leading-tight">{unit.name}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">{unit.bedrooms}BR &bull; R{(unit.base_price || 0).toLocaleString()} &bull; {unit.max_guests}</p>
                        <p className="text-[10px] text-slate-400">sync</p>
                      </div>
                      <div className="flex gap-0.5 mt-0.5 shrink-0">
                        <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center"><Building2 className="w-2.5 h-2.5 text-blue-600" /></div>
                        <div className="w-5 h-5 rounded-full bg-rose-100 flex items-center justify-center"><Home className="w-2.5 h-2.5 text-rose-500" /></div>
                      </div>
                    </div>

                    {/* Timeline area */}
                    <div className="relative" style={{ height: rowH }}>
                      {/* Day grid lines */}
                      <div className="flex absolute inset-0">
                        {days.map((day, i) => (
                          <div
                            key={i}
                            className={`border-r border-slate-100 ${isWeekend(day) ? 'bg-slate-50/50' : ''}`}
                            style={{ width: DAY_W, minWidth: DAY_W, height: '100%' }}
                          />
                        ))}
                      </div>

                      {/* Reservation & block bars — stacked by lane */}
                      {bars.map(bar => {
                        const SKEW = 6;
                        const GAP = 10;
                        const barW = Math.max(bar.width - GAP, 8);
                        const lane = bar._lane || 0;
                        const barH = 22;
                        const barTop = 4 + lane * (barH + 4);
                        const isClickable = !bar.id.startsWith('block-');
                        return (
                          <div
                            key={bar.id}
                            className={`absolute flex items-center overflow-hidden text-[10px] font-medium whitespace-nowrap ${isClickable ? 'cursor-pointer hover:opacity-80' : ''} ${bar.status === 'cancelled' ? 'opacity-40 line-through' : ''}`}
                            style={{
                              left: bar.left + 5,
                              width: barW,
                              top: barTop,
                              height: barH,
                              backgroundColor: bar.bg,
                              color: bar.text,
                              clipPath: `polygon(${SKEW}px 0%, calc(100% - ${SKEW}px) 0%, 100% 50%, calc(100% - ${SKEW}px) 100%, ${SKEW}px 100%, 0% 50%)`,
                              paddingLeft: SKEW + 4,
                              paddingRight: SKEW + 4,
                              zIndex: 5 + lane,
                            }}
                            title={bar.label}
                            onClick={() => isClickable && handleBarClick(bar)}
                            data-testid={`cal-bar-${bar.id}`}
                          >
                            <span className="truncate">{bar.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {prop.units.length === 0 && (
                <div className="flex items-center justify-center py-6 text-xs text-slate-400">No units in this property</div>
              )}
            </div>
          ))}

          {calendarData.length === 0 && (
            <div className="flex items-center justify-center py-16 text-sm text-slate-400">No properties found</div>
          )}
        </div>
      </div>

      {/* Booking Details Dialog */}
      <Dialog open={!!selectedBooking} onOpenChange={(open) => !open && setSelectedBooking(null)}>
        <DialogContent className="max-w-md" data-testid="booking-details-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-slate-900">Booking Details</DialogTitle>
          </DialogHeader>
          {selectedBooking && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Guest</span>
                  <span className="text-sm font-bold text-slate-900">{selectedBooking.guest_name || selectedBooking.label || '—'}</span>
                </div>
                <div className="border-t border-slate-200" />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Unit</span>
                  <span className="text-sm text-slate-700">{selectedBooking.unit_name || '—'}</span>
                </div>
                <div className="border-t border-slate-200" />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Check-in</span>
                  <span className="text-sm text-slate-700">{selectedBooking.check_in ? format(new Date(selectedBooking.check_in), 'EEE, MMM dd, yyyy') : '—'}</span>
                </div>
                <div className="border-t border-slate-200" />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Check-out</span>
                  <span className="text-sm text-slate-700">{selectedBooking.check_out ? format(new Date(selectedBooking.check_out), 'EEE, MMM dd, yyyy') : '—'}</span>
                </div>
                <div className="border-t border-slate-200" />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Nights</span>
                  <span className="text-sm font-medium text-slate-700">{selectedBooking.nights || '—'}</span>
                </div>
                <div className="border-t border-slate-200" />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Source</span>
                  <Badge variant="outline" className="text-[10px]">{selectedBooking.source || '—'}</Badge>
                </div>
                <div className="border-t border-slate-200" />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Status</span>
                  <Badge className={`text-[10px] ${selectedBooking.status === 'confirmed' ? 'bg-emerald-100 text-emerald-700' : selectedBooking.status === 'cancelled' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                    {selectedBooking.status}
                  </Badge>
                </div>
                {selectedBooking.notes && (
                  <>
                    <div className="border-t border-slate-200" />
                    <div className="flex items-start justify-between">
                      <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Notes</span>
                      <span className="text-sm text-slate-600 text-right max-w-[60%]">{selectedBooking.notes}</span>
                    </div>
                  </>
                )}
              </div>

              {/* Cancel button — only for non-iCal, non-cancelled, admin/manager */}
              {canCancel && !selectedBooking.source?.startsWith('ical') && selectedBooking.status !== 'cancelled' && (
                <div className="pt-2 border-t border-slate-100">
                  <Button className="w-full bg-red-600 hover:bg-red-700 text-white" onClick={handleCancelBooking} disabled={cancelLoading} data-testid="cancel-booking-confirm">
                    {cancelLoading ? <span className="animate-spin mr-2">⏳</span> : <XCircle className="w-4 h-4 mr-1.5" />}Cancel This Booking
                  </Button>
                </div>
              )}

              {selectedBooking.source?.startsWith('ical') && (
                <>
                  <p className="text-xs text-slate-400 text-center italic">This booking was synced from {selectedBooking.source.replace('ical_', '')}. To modify it, update it on the original platform.</p>
                  {canCancel && (
                    <div className="pt-2 border-t border-slate-100">
                      <Button
                        variant="outline"
                        className="w-full border-red-300 text-red-700 hover:bg-red-50"
                        onClick={handleDeleteAndHide}
                        disabled={deleteLoading}
                        data-testid="delete-ical-booking-btn"
                      >
                        {deleteLoading ? <span className="animate-spin mr-2">⏳</span> : <XCircle className="w-4 h-4 mr-1.5" />}Delete & Hide From Future Syncs
                      </Button>
                      <p className="text-[10px] text-slate-400 text-center mt-1.5 italic">Removes from RentalHub only. Original platform unaffected.</p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
